// src/pages/Users/News/CreateNewsModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload, FaTrash } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateNews, deleteNews } from "../../../services/newsServices";
import { useUser } from "../../Home/UserContext";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ⬇️ Nouveaux services pour upload local (Sharp)
import {
  uploadNewsPhoto,
  toAbsolutePhotoUrl,
} from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

// Quill config (sobre)
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "clean"];

export default function CreateNewsModal({ isOpen, onClose, onCreateSuccess, news }) {
  const { user } = useUser();

  // État du formulaire
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    image_url: "",
    author_id: user?.userId || "",
    is_active: true,
  });

  // États UI
  const [saving, setSaving] = useState(false);

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // "webp" | "jpg" | "png"

  const dialogRef = useRef(null);
  const newsId = news?.id ?? null;

  // Snapshot initial (pour “dirty”)
  const initial = useMemo(
    () => ({
      title: news?.title ?? "",
      content: news?.content ?? "",
      image_url: news?.image_url ?? "",
      author_id: news?.author_id ?? user?.userId ?? "",
      is_active: news?.is_active ?? true,
    }),
    [news, user?.userId]
  );

  // Hydrate form quand la news change
  useEffect(() => {
    setFormData(initial);
  }, [initial]);

  // Lock scroll + ESC + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='title']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen]);

  const isDirty =
    formData.title !== initial.title ||
    formData.content !== initial.content ||
    formData.image_url !== initial.image_url ||
    formData.is_active !== initial.is_active;

  const handleClose = () => {
    if (uploading || saving) return;
    if (!isDirty || window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")) {
      onClose?.();
    }
  };

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Champs form
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
  const handleContentChange = (value) => setFormData((p) => ({ ...p, content: value }));

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newsId) {
      toast.error("Aucun brouillon d’actualité n’a été initialisé. Cliquez d’abord sur “Nouvelle actu”.");
      return;
    }
    const title = formData.title.trim();
    if (!title) {
      toast.warn("Le titre est requis.");
      dialogRef.current?.querySelector("input[name='title']")?.focus();
      return;
    }

    try {
      setSaving(true);
      const payload = {
        id: newsId,
        title,
        content: formData.content,
        image_url: formData.image_url,
        author_id: formData.author_id || user?.userId,
        is_active: formData.is_active ? 1 : 0,
      };
      await updateNews(payload);
      toast.success("Actualité enregistrée.");
      onCreateSuccess?.();
    } catch (err) {
      console.error("Erreur update news:", err);
      toast.error("Erreur lors de l’enregistrement de l’actualité.");
    } finally {
      setSaving(false);
    }
  };

  // Upload image (local sharp)
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newsId) {
      toast.error("Aucun brouillon pour associer l’image.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    // Validation côté front
    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadNewsPhoto(newsId, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      // Mise à jour de l'URL (le back renvoie /uploads/...)
      setFormData((p) => ({ ...p, image_url: updated?.image_url || p.image_url }));
      toast.success("Image téléversée et optimisée.");
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (err) {
      console.error("Upload image news:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  // 🖼️ Toujours transformer /uploads/... en URL absolue exploitable par <img>
  const cover = toAbsolutePhotoUrl(formData.image_url?.trim() ? formData.image_url : FALLBACK);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Créer une actualité"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Créer une actualité
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Renseignez le titre, le contenu et l’illustration.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Preview + upload */}
          <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] gap-5">
            <div className="flex flex-col gap-3">
              <div className="relative h-40 sm:h-48 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                <img
                  src={cover}
                  alt="Illustration actualité"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK;
                  }}
                  className="h-full w-full object-contain"
                />
              </div>
              <Button
                type="button"
                text="Téléverser une image"
                icon={FaUpload}
                onClick={() => setIsUploadOpen(true)}
                className="!rounded-xl gap-2"
              />
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="Rempli automatiquement après upload"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                readOnly
              />
            </div>

            {/* Champs texte */}
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Titre <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex. : Résultats tournoi régional"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                  maxLength={160}
                />
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {formData.title.length}/160 caractères
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Contenu
                </label>
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez l’actualité…"
                    className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 px-3 py-2 w-fit">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="accent-violet-500"
                />
                <span className="text-sm text-slate-800 dark:text-slate-100">
                  Rendre l’actualité visible
                </span>
              </label>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 -mx-4 sm:-mx-6 pt-4">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              {newsId && (
                <Button
                  type="button"
                  text="Supprimer"
                  icon={FaTrash}
                  onClick={async () => {
                    if (!window.confirm("Supprimer définitivement cette actualité ?")) return;
                    try {
                      await deleteNews(newsId);
                      toast.success("Actualité supprimée.");
                      onClose?.();
                      onCreateSuccess?.();
                    } catch (err) {
                      console.error("Erreur suppression actualité:", err);
                      toast.error("Erreur lors de la suppression.");
                    }
                  }}
                  className="!rounded-xl !bg-rose-100 !text-rose-700 hover:!bg-rose-200 dark:!bg-rose-900/40 dark:!text-rose-200 gap-2"
                />
              )}
              <Button
                type="button"
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button
                type="submit"
                text={saving ? "Enregistrement…" : "Enregistrer"}
                icon={FaSave}
                className="!rounded-xl gap-2"
                disabled={saving}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Sous-modale upload image (local) */}
      {isUploadOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.currentTarget === e.target && setIsUploadOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Téléverser une image"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
                Téléverser une image (optimisée ≤1600px)
              </h4>
              {!newsId && (
                <p className="text-sm text-rose-600 mb-3">
                  Aucun brouillon d’actualité n’est disponible pour associer l’image.
                </p>
              )}
              <form onSubmit={handleUpload} className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                <div className="flex items-center gap-2">
                  <span className="text-sm">Format</span>
                  <select
                    className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="webp">WEBP (recommandé)</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>

                {uploading && (
                  <div>
                    <progress className="progress w-full" value={uploadProgress} max="100" />
                    <div className="text-xs mt-1">{uploadProgress}%</div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    text="Fermer"
                    icon={FaTimes}
                    onClick={() => setIsUploadOpen(false)}
                    className="!rounded-xl"
                  />
                  <Button
                    type="submit"
                    text={uploading ? "Transfert…" : "Sauvegarder"}
                    icon={FaSave}
                    className="!rounded-xl"
                    disabled={uploading || !selectedFile || !newsId}
                  />
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}
